<template>
  <div class="container max-w-lg mt-12">
    <app-badge
      :icon="isActive ? 'check' : 'close-circle'"
      :type="
        isActive
          ? 'positive'
          : companiesHouse.company_status
          ? 'negative'
          : 'neutral'
      "
    >
      {{ companiesHouse.company_status || "Unverified" | sentencecase }}
    </app-badge>
    <h2 class="text-xl">
      {{ company.name | sentencecase }}
      <span v-if="company.number">[{{ company.number }}]</span>
    </h2>
    <div v-if="requestErrors.message" class="py-4 text-negative-700">
      {{ requestErrors.message }}
    </div>
    <p
      v-if="companiesHouse.date_of_cessation"
      class="text-sm font-bold text-negative-800"
    >
      Dissolved on: {{ formatDate(companiesHouse.date_of_cessation) }}
    </p>
    <p class="italic" v-html="companyAddress" />
    <p class="mt-2">
      <icon-link
        :href="`https://beta.companieshouse.gov.uk/company/${company.number}`"
        target="_blank"
        rel="noopener"
        icon="external-window"
      >
        Companies House
      </icon-link>
    </p>
    <div class="flex flex-wrap my-12 -mx-6">
      <div class="w-full md:w-1/2 px-6">
        <app-badge v-if="accounts.overdue" icon="close-circle" type="negative">
          Overdue
        </app-badge>
        <h3 class="mb-2">
          Accounts
        </h3>
        <p v-if="isActive" class="mb-2">
          Next accounts made up to
          <strong>{{ formatDate(accounts.next_made_up_to) }}</strong> due by
          <strong>{{ formatDate(accounts.next_due) }}</strong
          >.
        </p>
        <p v-if="accounts && accounts.last_accounts">
          Last accounts made up to
          <strong>{{ formatDate(accounts.last_accounts.made_up_to) }}</strong
          >.
        </p>
      </div>
      <div v-if="confirmation_statement" class="w-full md:w-1/2 px-6">
        <app-badge
          v-if="confirmation_statement.overdue"
          icon="close-circle"
          type="negative"
        >
          Overdue
        </app-badge>
        <h3 class="mb-2">
          Confirmation statement
        </h3>
        <p class="mb-2">
          Next statement date
          <strong>{{
            formatDate(confirmation_statement.next_made_up_to)
          }}</strong>
          due by
          <strong>{{ formatDate(confirmation_statement.next_due) }}</strong
          >.
        </p>
        <p>
          Last statement dated
          <strong>{{
            formatDate(confirmation_statement.last_made_up_to)
          }}</strong
          >.
        </p>
      </div>
    </div>
    <div v-if="SICValue">
      <h3 class="mt-4">
        Nature of business (SIC)
      </h3>
      <p v-html="SICValue" />
    </div>
    <div class="mt-6 max-w-25" v-if="company.number">
      <app-button @click="updateCompany" :disabled="updating"
        >Update Company Details</app-button
      >
    </div>
  </div>
</template>

<script>
import { SIC } from "../../store/sic";
import pick from "lodash/pick";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { requestMixin } from "../../mixins/request";

export default {
  name: "CompanyProfile",
  mixins: [requestMixin],
  data() {
    return {
      company: this.state.company,
      updating: false
    };
  },
  computed: {
    companiesHouse() {
      let res = this.company.companies_house_info ?? "{}"
      try {
        res = JSON.parse(this.company.companies_house_info);
      } catch (e) {
        // Likely already json

        // eslint-disable-next-line no-console
        console.error(e);
      }
      return res
    },
    companyAddress() {
      let address = pick(this.company, [
        "address_line_1",
        "address_line_2",
        "town",
        "county",
        "postcode"
      ]);
      return Object.values(address)
        .filter(Boolean)
        .join("<br>");
    },
    accounts() {
      return this.companiesHouse?.accounts || false;
    },
    confirmation_statement() {
      return this.companiesHouse.confirmation_statement || false;
    },
    isActive() {
      return this.companiesHouse?.company_status === "active";
    },
    SICValue() {
      const company_sic = this.companiesHouse.sic_codes;
      const sic = SIC;
      if (!company_sic) return;
      return company_sic
        .reduce((arr, code) => {
          arr.push(`<strong>${code}</strong>: ${sic.code(code)}`);
          return arr;
        }, [])
        .join("<br>");
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return "__/__/____";
      return format(parseISO(date), "do MMMM yyyy");
    },
    updateCompany() {
      this.updating = true;
      axios
        .post(window.location)
        .then(response => (this.company = response.data))
        .catch(error => this.requestError(error))
        .then(() => (this.updating = false));
    }
  }
};
</script>
